/**
 * Complete logic for the quiz for Intelligent Bookseller
 */

/**
 * Validate the quiz by checking all required questions
 * @param {NodeList} quizRequiredQuestions - The required questions
 * @param {HTMLElement} container - The container to find the global error messages
 * @returns {boolean} true if the quiz is valid, false otherwise
 */
export function quizValidate(quizRequiredQuestions, container) {
  let quizIsValid = true;

  // Validate required questions
  quizRequiredQuestions.forEach((question) => {
    const questionRequiredErrorMsg = question.querySelector('[data-question-required-error-msg]');

    if (questionRequiredErrorMsg) {
      const questionType = question.dataset.questionType;
      let questionIsValid = true;

      // Check if this specific question is valid
      if (questionType === 'text') {
        const questionTextarea = question.querySelector('textarea');
        if (questionTextarea.value.trim() === '') {
          questionIsValid = false;
          quizIsValid = false;
        }
      } else if (questionType === 'checkbox' || questionType === 'radio') {
        const questionInputChecked = question.querySelector('input:checked');
        if (questionInputChecked === null) {
          questionIsValid = false;
          quizIsValid = false;
        }
      }

      // Show or hide error message for this specific question
      if (questionIsValid === false) {
        questionRequiredErrorMsg.removeAttribute('aria-hidden');
      } else {
        questionRequiredErrorMsg.setAttribute('aria-hidden', 'true');
      }
    }
  });

  // Validate global required questions error messages
  const globalRequiredQuestionsErrorMsg = container.querySelector('[data-global-required-questions-error-msg]');
  if (globalRequiredQuestionsErrorMsg) {
    if (quizIsValid === false) {
      globalRequiredQuestionsErrorMsg.removeAttribute('aria-hidden');
    } else {
      globalRequiredQuestionsErrorMsg.setAttribute('aria-hidden', 'true');
    }
  }

  return quizIsValid;
}

/**
 * Check if at least one advanced question has been answered
 * @param {NodeList} quizAdvancedQuestions - The advanced questions
 * @returns {boolean} true if at least one advanced question has been answered
 */
export function hasAdvancedQuestionsAnswered(quizAdvancedQuestions) {
  // Check if at least one advanced question has been answered
  if (quizAdvancedQuestions.length === 0) {
    return false;
  }

  for (const question of quizAdvancedQuestions) {
    const questionType = question.dataset.questionType;

    if (questionType === 'text') {
      // Check if textarea has non-empty value
      const textarea = question.querySelector('textarea');
      if (textarea && textarea.value.trim() !== '') {
        return true;
      }
    } else if (questionType === 'checkbox') {
      // Check if at least one checkbox is checked
      const checkedInputs = question.querySelectorAll('input:checked');
      if (checkedInputs.length > 0) {
        return true;
      }
    } else if (questionType === 'radio') {
      // Check if a radio button is checked
      const checkedInput = question.querySelector('input:checked');
      if (checkedInput !== null) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Hide all error messages
 * @param {NodeList} quizRequiredQuestions - The required questions
 * @param {HTMLElement} container - The container to find the error messages
 */
export function hideAllErrorMessages(quizRequiredQuestions, container) {
  // Hide all individual question error messages
  quizRequiredQuestions.forEach((question) => {
    const questionRequiredErrorMsg = question.querySelector('[data-question-required-error-msg]');
    if (questionRequiredErrorMsg) {
      questionRequiredErrorMsg.setAttribute('aria-hidden', 'true');
    }
  });

  // Hide global required questions error message
  const globalRequiredQuestionsErrorMsg = container.querySelector('[data-global-required-questions-error-msg]');
  if (globalRequiredQuestionsErrorMsg) {
    globalRequiredQuestionsErrorMsg.setAttribute('aria-hidden', 'true');
  }

  // Hide server error message
  const serverErrorMsg = container.querySelector('[data-server-error-msg]');
  if (serverErrorMsg) {
    serverErrorMsg.setAttribute('aria-hidden', 'true');
  }
}

/**
 * Collect the quiz data from the DOM
 * @param {NodeList} quizQuestions - All the questions of the quiz
 * @returns {Object} The questionnaire object with all the collected data
 */
export function collectQuizData(quizQuestions) {
  const questionnaire = {};

  quizQuestions.forEach((questionElement) => {
    const questionHandle = questionElement.dataset.question;
    const questionType = questionElement.dataset.questionType;

    if (!questionHandle) return;

    let value = '';

    if (questionType === 'text') {
      const textarea = questionElement.querySelector('textarea');
      value = textarea ? textarea.value.trim() : '';
    } else if (questionType === 'checkbox') {
      const checkedInputs = questionElement.querySelectorAll('input:checked');
      value = Array.from(checkedInputs).map(input => input.value);
    } else if (questionType === 'radio') {
      const checkedInput = questionElement.querySelector('input:checked');
      value = checkedInput ? checkedInput.value : '';
    }

    // Map question handles to payload keys
    if (questionHandle === 'genre') {
      questionnaire.genre = Array.isArray(value) ? value : (value ? [value] : []);
    } else if (questionHandle === 'purpose') {
      questionnaire.purpose = value || '';
    } else if (questionHandle === 'ageProfile') {
      questionnaire.ageProfile = value || '';
    } else if (questionHandle === 'interests') {
      questionnaire.interests = value || '';
    } else if (questionHandle === 'hasSimilarBook') {
      questionnaire.hasSimilarBook = value || '';
    } else if (questionHandle === 'similarBookTitle') {
      questionnaire.similarBookTitle = value || '';
    } else if (questionHandle === 'similarBookAuthor') {
      questionnaire.similarBookAuthor = value || '';
    } else if (questionHandle === 'similarBookSeries') {
      questionnaire.similarBookSeries = value || '';
    } else if (questionHandle === 'abandonedBook') {
      questionnaire.abandonedBook = value || '';
    } else if (questionHandle === 'readingMotivation') {
      questionnaire.readingMotivation = value || '';
    } else if (questionHandle === 'complexity') {
      questionnaire.complexity = value || '';
    } else if (questionHandle === 'recency') {
      questionnaire.recency = value || '';
    } else if (questionHandle === 'length') {
      questionnaire.length = value || '';
    } else {
      questionnaire[questionHandle] = value || '';
    }
  });

  // Build similarBook object
  const hasReference = questionnaire.hasSimilarBook === 'Oui' && 
                      (questionnaire.similarBookTitle || questionnaire.similarBookAuthor || questionnaire.similarBookSeries);
  
  questionnaire.similarBook = {
    hasReference: hasReference || false,
    title: questionnaire.similarBookTitle || '',
    author: questionnaire.similarBookAuthor || '',
    series: questionnaire.similarBookSeries || ''
  };

  // Ensure genre is an array
  if (!questionnaire.genre) {
    questionnaire.genre = [];
  }

  // Set default empty strings for optional fields if not present
  const optionalFields = ['interests', 'abandonedBook', 'similarBookTitle', 'similarBookAuthor', 'similarBookSeries', 'readingMotivation', 'complexity', 'recency', 'length'];
  optionalFields.forEach(field => {
    if (!questionnaire[field]) {
      questionnaire[field] = '';
    }
  });

  return questionnaire;
}

/**
 * Restore the quiz data in the DOM
 * @param {Object} questionnaireData - The questionnaire data to restore
 * @param {NodeList} quizQuestions - All the questions of the quiz
 */
export function restoreQuizData(questionnaireData, quizQuestions) {
  if (!questionnaireData || !quizQuestions) {
    return;
  }

  quizQuestions.forEach((questionElement) => {
    const questionHandle = questionElement.dataset.question;
    const questionType = questionElement.dataset.questionType;

    if (!questionHandle || !questionnaireData[questionHandle]) {
      return;
    }

    const value = questionnaireData[questionHandle];

    if (questionType === 'text') {
      const textarea = questionElement.querySelector('textarea');
      if (textarea && value) {
        textarea.value = value;
      }
    } else if (questionType === 'checkbox') {
      // Uncheck all first
      const allInputs = questionElement.querySelectorAll('input[type="checkbox"]');
      allInputs.forEach(input => input.checked = false);
      
      // Check the ones that match the saved values
      if (Array.isArray(value) && value.length > 0) {
        value.forEach(savedValue => {
          const input = questionElement.querySelector(`input[value="${savedValue}"]`);
          if (input) {
            input.checked = true;
            // Trigger change event to update conditional questions
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    } else if (questionType === 'radio') {
      // Uncheck all first
      const allInputs = questionElement.querySelectorAll('input[type="radio"]');
      allInputs.forEach(input => input.checked = false);
      
      // Check the one that matches the saved value
      if (value) {
        const input = questionElement.querySelector(`input[value="${value}"]`);
        if (input) {
          input.checked = true;
          // Trigger change event to update conditional questions
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  });
}

/**
 * Initialize the advanced questions with the toggle button
 * @param {NodeList} quizAdvancedQuestions - The advanced questions
 * @param {HTMLElement} toggleBtn - The button to toggle the advanced questions
 * @param {NodeList} toggleTexts - The texts of the toggle button
 * @param {Object} state - The state object to store the state
 * @returns {Function|null} The handler created (to be able to remove it later)
 */
export function initQuizAdvancedQuestions(quizAdvancedQuestions, toggleBtn, toggleTexts, state) {
  if (quizAdvancedQuestions.length > 0 && toggleBtn && toggleTexts.length > 0) {
    // Store the handler to be able to remove it later
    const handler = () => {
      // Update state
      state.quizAdvancedQuestionsShown = !state.quizAdvancedQuestionsShown;
      
      // Show/Hide advanced questions
      quizAdvancedQuestions.forEach((question) => {
        if (state.quizAdvancedQuestionsShown) {
          // Check if conditional question is open
          if (question.hasAttribute('data-conditional-question-is-open')) {
            if (question.getAttribute('data-conditional-question-is-open') === 'true') {
              question.removeAttribute('aria-hidden');
            }
          } else {
            // If not conditional question, show question
            question.removeAttribute('aria-hidden');
          }
        } else {
          // Hide question
          question.setAttribute('aria-hidden', 'true');
        }
      });

      // Update toggle button text
      toggleTexts.forEach((text) => {
        if (text.getAttribute('aria-hidden') === 'true') {
          text.removeAttribute('aria-hidden');
        } else {
          text.setAttribute('aria-hidden', 'true');
        }
      });
    };
    
    toggleBtn.addEventListener('click', handler);
    return handler;
  }
  return null;
}

/**
 * Initialize the conditional questions
 * @param {NodeList} conditionalQuestions - The conditional questions
 * @param {HTMLElement} container - The container to find the parent elements
 * @returns {Array} The array of handlers created (to be able to remove them later)
 */
export function initConditionalQuestions(conditionalQuestions, container) {
  const handlers = [];
  
  if (conditionalQuestions.length > 0) {
    conditionalQuestions.forEach((question) => {
      const parentQuestionHandle = question.dataset.parentQuestionHandle;
      const conditionalAnswer = question.dataset.conditionalAnswer;

      if (parentQuestionHandle && conditionalAnswer) {
        const inputElements = container.querySelectorAll(`[data-question="${parentQuestionHandle}"] input`);

        if (inputElements.length > 0) {
          inputElements.forEach((inputElement) => {
            // Store the handler to be able to remove it later
            const handler = () => {
              // Check if conditional answer is checked
              const inputChecked = container.querySelector(`[data-question="${parentQuestionHandle}"] input:checked`);
              if (conditionalAnswer.includes('|')) {
                // When multiple answers are possible
                const conditionalAnswers = conditionalAnswer.split('|');

                if (inputChecked && conditionalAnswers.includes(inputChecked.value)) {
                  question.removeAttribute('aria-hidden');
                  question.setAttribute('data-conditional-question-is-open', 'true');
                } else {
                  question.setAttribute('aria-hidden', 'true');
                  question.setAttribute('data-conditional-question-is-open', 'false');
                }
              } else {
                // When only one answer is possible
                if (inputChecked && inputChecked.value === conditionalAnswer) {
                  question.removeAttribute('aria-hidden');
                  question.setAttribute('data-conditional-question-is-open', 'true');
                } else {
                  question.setAttribute('aria-hidden', 'true');
                  question.setAttribute('data-conditional-question-is-open', 'false');
                }
              }
            };
            
            inputElement.addEventListener('change', handler);
            // Store the reference to be able to remove it later
            handlers.push({ element: inputElement, handler });
          });
        }
      }
    });
  }
  
  return handlers;
}

/**
 * Initialize the handler of the quiz submission
 * @param {HTMLFormElement} quiz - The quiz form
 * @param {Object} callbacks - The callbacks necessary
 * @param {Function} callbacks.hideAllErrorMessages - Function to hide the error messages
 * @param {Function} callbacks.toggleLoader - Function to toggle the loader
 * @param {Function} callbacks.quizValidate - Function to validate the quiz
 * @param {Function} callbacks.buildPayload - Function to build the payload
 * @param {Function} callbacks.fetchMagicCartSuggestions - Function to call the API
 * @param {Function} callbacks.setSuggestionsContent - Function to display the suggestions
 * @param {Function} callbacks.setContent - Function to change the display
 * @param {Function} callbacks.saveState - Function to save the state
 * @param {Function} callbacks.hasAdvancedQuestionsAnswered - Function to check the advanced questions
 * @param {Object} state - The state object to store the state
 * @param {HTMLElement} container - The container to find the elements
 * @returns {Function} The handler created (to be able to remove it later)
 */
export function handleQuizSubmit(quiz, callbacks, state, container) {
  const handler = async (e) => {
    e.preventDefault();
    // Hide all error messages at the start of each new submission
    callbacks.hideAllErrorMessages();
    // Start loading
    callbacks.toggleLoader();
    
    // Validate quiz
    const quizIsValid = callbacks.quizValidate();
    if (quizIsValid === false) {
      // Stop loading
      callbacks.toggleLoader();

      return false;
    } else {
      try {
        // Build payload
        const payload = await callbacks.buildPayload();

        // Fetch suggestions from API
        const apiResponse = await callbacks.fetchMagicCartSuggestions(payload);
        
        // Store response for later use
        state.apiResponse = apiResponse;
        state.quizSubmitted = true;
        
        // Save state after successful submission
        callbacks.saveState();

        // Set suggestions content
        await callbacks.setSuggestionsContent(apiResponse);
        
        // Update content to show suggestions
        callbacks.setContent();
      } catch (error) {
        console.error('Error submitting quiz:', error);

        // Show server error messages
        const serverErrorMsg = container.querySelector('[data-server-error-msg]');
        if (serverErrorMsg) {
          serverErrorMsg.removeAttribute('aria-hidden');
        }
      } finally {
        // Stop loading
        callbacks.toggleLoader();
      }
    }

    // Intelligent Bookseller: data layer event
    const hasAdvancedQuestionsFound = callbacks.hasAdvancedQuestionsAnswered();
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'submit_intelligent_library',
      advanced_questions: hasAdvancedQuestionsFound // true or false
    });
  };
  
  quiz.addEventListener('submit', handler);
  return handler;
}
